/**
 * Knowledge Base · General Knowledge — company knowledge the assistant can
 * reference in chat, organized into BUCKETS (who can read what). Rules are
 * short curated facts; reference files are documents indexed as uploaded.
 * With one bucket the grouping collapses to a single labeled header; bucket
 * #2 makes the structure appear.
 *
 * The rule list is display-only; a row opens /knowledge/rules/:ruleId.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Input,
  Select,
  Skeleton,
  Table,
} from '@servicecompressionllc/sc-design-system';
import {
  ChevronDown,
  ChevronRight,
  Lock,
  Plus,
  Upload,
} from '@servicecompressionllc/sc-design-system/icons';
import { useMe } from '../../hooks/useMe.js';
import { canWriteKnowledge } from '../../lib/knowledgeAccess.js';
import { isSuperAdmin } from '../../lib/roles.js';
import { knowledgeApi, type KnowledgeBucket, type KnowledgeRule } from './api.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { BucketSettingsTab } from './BucketSettingsTab.js';
import { AddToGkModal, type GkUploadKind } from './AddToGkModal.js';
import { CreateBucketModal } from './CreateBucketModal.js';
import { ReferenceFiles } from './ReferenceFiles.js';

/** The filename minus its extension — the prefilled title for an upload. */
const titleFromFilename = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  return (dot > 0 ? filename.slice(0, dot) : filename).trim();
};

export const RuleStatusBadge = ({ rule }: { rule: KnowledgeRule }) => {
  if (rule.status === 'PUBLISHED' && !rule.hasUnpublishedChanges) {
    return <Badge variant="success">published</Badge>;
  }
  if (rule.status === 'PUBLISHED') return <Badge variant="info">edited</Badge>;
  return <Badge variant="warning">draft</Badge>;
};

/** Overview mode shows a preview of each bucket; the ?bucket= view shows all.
 *  A 200-rule bucket must not own the page. */
const RULE_PREVIEW = 5;
const FILE_PREVIEW = 3;

const BucketHeader = ({
  bucket,
  expanded,
  onToggle,
  onOpen,
}: {
  bucket: KnowledgeBucket;
  expanded: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) => {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <button
        type="button"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${bucket.name}`}
        onClick={onToggle}
        className="flex items-center gap-1.5 text-[17px] font-semibold text-ink"
      >
        {expanded ? (
          <ChevronDown size={16} aria-hidden className="shrink-0 text-ink-subtle" />
        ) : (
          <ChevronRight size={16} aria-hidden className="shrink-0 text-ink-subtle" />
        )}
        {bucket.name}
      </button>

      {bucket.readGroups.length > 0 && (
        <Badge variant="neutral">
          <Lock size={11} aria-hidden className="mr-1.5 shrink-0" />
          {bucket.readGroups.map((g) => g.name).join(', ')}
        </Badge>
      )}
      <span className="ml-auto">
        <Button size="sm" variant="secondary" onClick={onOpen} aria-label={`Open ${bucket.name}`}>
          Open bucket
        </Button>
      </span>
    </div>
  );
};

export const RulesPage = () => {
  const me = useMe();
  const roles = me.data?.user.roles ?? [];
  const canWrite = me.data ? canWriteKnowledge(me.data.user) : false;
  const isTenantAdmin = roles.includes('TENANT_ADMIN') || isSuperAdmin(roles);
  const navigate = useNavigate();
  // /knowledge/buckets/:bucketId is the bucket's own page; /knowledge/rules is
  // the all-buckets overview. Same component — the param is the mode switch.
  const { bucketId: bucketParam = null } = useParams<{ bucketId: string }>();
  const rules = useQuery({ queryKey: ['knowledge', 'rules'], queryFn: knowledgeApi.rules });
  const buckets = useQuery({ queryKey: ['knowledge', 'buckets'], queryFn: knowledgeApi.buckets });
  const subjects = useQuery({
    queryKey: ['knowledge', 'subjects'],
    queryFn: knowledgeApi.subjects,
  });
  const docs = useQuery({
    queryKey: ['knowledge', 'generalDocuments'],
    queryFn: knowledgeApi.generalDocuments,
  });

  // The upload dialog offers two shelves: a curated rule (extract the text,
  // open a draft — nothing stored until saved) or a reference file (stored
  // and indexed as uploaded), landing in the chosen bucket.
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [creatingBucket, setCreatingBucket] = useState(false);
  // Accordion state — ephemeral UI, deliberately not in the URL. Buckets
  // start expanded; with many buckets, collapse what you're not working in.
  const [collapsedBuckets, setCollapsedBuckets] = useState<ReadonlySet<string>>(new Set());
  // Bucket-page tabs: content vs settings (settings is Tenant Admin work).
  const [bucketTab, setBucketTab] = useState<'content' | 'settings'>('content');
  const toggleBucket = (id: string) =>
    setCollapsedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const extract = useMutation({
    mutationFn: ({ file }: { file: File; bucketId: string | undefined }) =>
      knowledgeApi.extractRule(file),
    onSuccess: ({ text }, { file, bucketId }) =>
      void navigate('/knowledge/rules/new', {
        state: {
          extractedRule: {
            filename: file.name,
            title: titleFromFilename(file.name),
            text,
            bucketId: bucketId ?? null,
          },
        },
      }),
  });
  const uploadReference = useMutation({
    mutationFn: async ({
      file,
      bucketId,
      subjectIds,
    }: {
      file: File;
      bucketId: string | undefined;
      subjectIds: string[];
    }) => {
      const doc = await knowledgeApi.uploadGeneralDocument(file, bucketId);
      // The chosen equipment is saved right after; none = applies everywhere.
      return subjectIds.length > 0 ? knowledgeApi.setDocumentSubjects(doc.id, subjectIds) : doc;
    },
    onSuccess: () => {
      setAdding(false);
      void qc.invalidateQueries({ queryKey: ['knowledge'] });
    },
  });
  const createBucket = useMutation({
    mutationFn: (body: { name: string; readGroupIds: string[] }) => knowledgeApi.createBucket(body),
    onSuccess: () => {
      setCreatingBucket(false);
      void qc.invalidateQueries({ queryKey: ['knowledge', 'buckets'] });
    },
  });
  const submitUpload = (
    file: File,
    kind: GkUploadKind,
    bucketId: string | undefined,
    subjectIds: string[],
  ) => {
    extract.reset();
    uploadReference.reset();
    if (kind === 'rule') extract.mutate({ file, bucketId });
    else uploadReference.mutate({ file, bucketId, subjectIds });
  };
  const uploadBusy = extract.isPending || uploadReference.isPending;
  const uploadError =
    (extract.error as Error | null)?.message ??
    (uploadReference.error as Error | null)?.message ??
    null;

  // URL-backed so a filtered view is a link someone can send.
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const status = params.get('status') ?? 'all';
  const scope = params.get('scope') ?? 'all';
  const setParam = (key: string, value: string, defaultValue: string) => {
    setParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        if (value === defaultValue) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  if (rules.isLoading || buckets.isLoading || docs.isLoading) return <Skeleton className="h-40" />;
  if (rules.error || buckets.error) {
    return <div className="text-sm text-danger">Couldn’t load General Knowledge.</div>;
  }
  const allBuckets = buckets.data ?? [];
  const defaultBucketId = allBuckets.find((b) => b.isDefault)?.id ?? null;
  const currentBucket =
    bucketParam !== null ? allBuckets.find((b) => b.id === bucketParam) : undefined;
  if (bucketParam !== null && !currentBucket) {
    return <div className="text-sm text-danger">Couldn’t find this bucket.</div>;
  }
  const shownBuckets = currentBucket ? [currentBucket] : allBuckets;

  const all = rules.data ?? [];
  const q = search.trim().toLowerCase();
  const filtered = all.filter((r) => {
    if (q.length > 0 && !r.title.toLowerCase().includes(q)) return false;
    if (status === 'published' && !(r.status === 'PUBLISHED' && !r.hasUnpublishedChanges)) {
      return false;
    }
    if (status === 'edited' && !(r.status === 'PUBLISHED' && r.hasUnpublishedChanges)) return false;
    if (status === 'draft' && r.status !== 'DRAFT') return false;
    if (scope === 'global' && r.subjects.length > 0) return false;
    if (scope === 'scoped' && r.subjects.length === 0) return false;
    return true;
  });
  const rulesOf = (bucketId: string) =>
    filtered.filter((r) => (r.bucketId ?? defaultBucketId) === bucketId);
  const docsOf = (bucketId: string) =>
    (docs.data ?? []).filter((d) => (d.bucketId ?? defaultBucketId) === bucketId);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <section className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {currentBucket ? (
            <>
              <Breadcrumbs trail={[{ label: 'General Knowledge', to: '/knowledge/rules' }]} />
              <h2 className="text-2xl font-bold text-ink">{currentBucket.name}</h2>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-ink">General Knowledge</h2>
              <p className="text-sm text-ink-muted">
                Company knowledge the assistant can reference in chat, organized by who can read it.
              </p>
            </>
          )}
        </div>
        <span className="ml-auto flex shrink-0 gap-2">
          {isTenantAdmin && !currentBucket && (
            <Button variant="secondary" onClick={() => setCreatingBucket(true)}>
              <Plus size={14} aria-hidden className="mr-1.5 shrink-0" />
              New bucket
            </Button>
          )}
          {/* Uploads work from anywhere (the dialog picks or infers the
              bucket); RULES are authored from a bucket page, where the
              destination is the page you are standing on. */}
          {canWrite && (
            <Button variant="secondary" onClick={() => setAdding(true)}>
              <Upload size={14} aria-hidden className="mr-1.5 shrink-0" />
              Upload file
            </Button>
          )}
          {canWrite && currentBucket && (
            <Button
              onClick={() =>
                void navigate('/knowledge/rules/new', {
                  state: { newRuleBucketId: currentBucket.isDefault ? null : currentBucket.id },
                })
              }
            >
              New rule
            </Button>
          )}
        </span>
      </section>
      <AddToGkModal
        open={adding}
        busy={uploadBusy}
        error={adding ? uploadError : null}
        bucket={currentBucket}
        buckets={currentBucket ? undefined : allBuckets}
        onClose={() => {
          setAdding(false);
          extract.reset();
          uploadReference.reset();
        }}
        subjects={subjects.data ?? []}
        onSubmit={submitUpload}
      />
      <CreateBucketModal
        open={creatingBucket}
        busy={createBucket.isPending}
        error={creatingBucket ? ((createBucket.error as Error | null)?.message ?? null) : null}
        onClose={() => {
          setCreatingBucket(false);
          createBucket.reset();
        }}
        onSubmit={(body) => createBucket.mutate(body)}
      />

      {currentBucket && isTenantAdmin && (
        <div className="flex gap-1 border-b border-outline" role="tablist">
          {(
            [
              { id: 'content', label: 'Content' },
              { id: 'settings', label: 'Settings' },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={bucketTab === t.id}
              onClick={() => setBucketTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                bucketTab === t.id
                  ? 'border-primary text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {currentBucket && isTenantAdmin && bucketTab === 'settings' ? (
        <div role="tabpanel" aria-label="Settings">
          <BucketSettingsTab key={currentBucket.id} bucket={currentBucket} />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-72 shrink-0">
              <Input
                value={search}
                onChange={(e) => setParam('q', e.target.value, '')}
                placeholder="Find a rule…"
                aria-label="Find a rule"
                className="w-full"
              />
            </div>
            {currentBucket !== undefined && (
              <>
                <div className="w-44 shrink-0">
                  <Select
                    value={[status]}
                    onChange={(v) => setParam('status', v[0] ?? 'all', 'all')}
                    options={[
                      { value: 'all', label: 'Any status' },
                      { value: 'published', label: 'Published' },
                      { value: 'edited', label: 'Edited' },
                      { value: 'draft', label: 'Draft' },
                    ]}
                    aria-label="Filter by status"
                    searchable={false}
                  />
                </div>
                <div className="w-44 shrink-0">
                  <Select
                    value={[scope]}
                    onChange={(v) => setParam('scope', v[0] ?? 'all', 'all')}
                    options={[
                      { value: 'all', label: 'Anywhere' },
                      { value: 'global', label: 'Everywhere only' },
                      { value: 'scoped', label: 'Specific subjects' },
                    ]}
                    aria-label="Filter by where it applies"
                    searchable={false}
                  />
                </div>
              </>
            )}
          </div>

          {shownBuckets.map((bucket) => {
            const allBucketRules = rulesOf(bucket.id);
            const allBucketDocs = docsOf(bucket.id);
            // Overview mode previews each section; the single-bucket view is the
            // "own page" for detail and shows everything.
            const previewing = bucketParam === null;
            const bucketRules = previewing ? allBucketRules.slice(0, RULE_PREVIEW) : allBucketRules;
            const bucketDocs = previewing ? allBucketDocs.slice(0, FILE_PREVIEW) : allBucketDocs;
            const expanded = !collapsedBuckets.has(bucket.id);
            return (
              <section key={bucket.id} className="flex flex-col gap-3">
                {!currentBucket && (
                  <BucketHeader
                    bucket={bucket}
                    expanded={expanded}
                    onToggle={() => toggleBucket(bucket.id)}
                    onOpen={() => void navigate(`/knowledge/buckets/${bucket.id}`)}
                  />
                )}
                {(expanded || currentBucket !== undefined) && (
                  <div className="flex flex-col gap-3">
                    <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
                      Rules
                    </span>
                    {bucketRules.length === 0 ? (
                      <div className="rounded-lg border border-outline bg-surface-elevated p-4 text-sm text-ink-subtle">
                        {all.length > 0 && filtered.length !== all.length
                          ? 'Nothing matches those filters.'
                          : 'No rules in this bucket yet.'}
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-outline bg-surface-elevated">
                        <Table.Root>
                          <Table.Header>
                            <Table.Row>
                              <Table.HeaderCell>Rule</Table.HeaderCell>
                              <Table.HeaderCell>Applies to</Table.HeaderCell>
                              <Table.HeaderCell>Status</Table.HeaderCell>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {bucketRules.map((r) => (
                              <Table.Row
                                key={r.id}
                                className="cursor-pointer hover:bg-surface-hover"
                                onClick={() => void navigate(`/knowledge/rules/${r.id}`)}
                              >
                                <Table.Cell>
                                  <span className="line-clamp-1 text-ink" title={r.title}>
                                    {r.title}
                                  </span>
                                </Table.Cell>
                                <Table.Cell className="whitespace-nowrap text-ink-muted">
                                  {r.subjects.length === 0
                                    ? 'Everywhere'
                                    : r.subjects.map((s) => s.name).join(', ')}
                                </Table.Cell>
                                <Table.Cell className="whitespace-nowrap">
                                  <RuleStatusBadge rule={r} />
                                </Table.Cell>
                              </Table.Row>
                            ))}
                            {previewing && allBucketRules.length > bucketRules.length && (
                              <Table.Row
                                className="cursor-pointer hover:bg-surface-hover"
                                onClick={() => void navigate(`/knowledge/buckets/${bucket.id}`)}
                              >
                                <Table.Cell
                                  className="text-sm text-ink-subtle hover:text-ink"
                                  colSpan={3}
                                >
                                  …and {allBucketRules.length - bucketRules.length} more{' '}
                                  {allBucketRules.length - bucketRules.length === 1
                                    ? 'rule'
                                    : 'rules'}{' '}
                                  — open the bucket to see everything
                                </Table.Cell>
                              </Table.Row>
                            )}
                          </Table.Body>
                        </Table.Root>
                      </div>
                    )}
                    <ReferenceFiles rows={bucketDocs} bucketId={bucket.id} />
                    {previewing && allBucketDocs.length > bucketDocs.length && (
                      <Link
                        to={`/knowledge/buckets/${bucket.id}`}
                        className="text-sm text-ink-subtle hover:text-ink hover:underline"
                      >
                        …and {allBucketDocs.length - bucketDocs.length} more{' '}
                        {allBucketDocs.length - bucketDocs.length === 1
                          ? 'reference file'
                          : 'reference files'}
                      </Link>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </>
      )}
    </div>
  );
};
